/* eslint-disable @next/next/no-img-element */
import type { PurchasedProductDetail } from "@/hooks/usePurchasedProductDetail";
import type { PurchasedProduct } from "@/hooks/usePurchasedProducts";
import {
  fetchPurchasedProductDetail,
  hasCustomerId,
  previewSubscriptionMergeShipping,
  previewSubscriptionShipping,
  reactivateStayAiSubscription,
  setShippingAddressStayAiSubscription,
  type StayAiShippingAddressInput,
} from "@/lib/api";
import {
  getDiscountCode,
  getStoredEmail,
  isShopifyLoggedIn,
} from "@/lib/emailStorage";
import { createCheckoutUrl } from "@/lib/checkoutUrl";
import {
  isWelcomeCodeApplied,
  markWelcomeOfferApplyPending,
  WELCOME_OFFER_ENABLED,
} from "@/lib/welcomeOffer";
import type { DefaultPanelTab } from "@/lib/panelConfig";
import type { WidgetOpenSource } from "@/lib/widgetEvents";
import {
  getShopForTracking,
  setCombineNotificationId,
  setSizeUpNotificationId,
  trackBuyAgainWidgetInteract,
} from "@/lib/tracking";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Gift,
  Loader2,
  Package,
  RefreshCw,
  // ShoppingBag — re-add when restoring the temporarily-hidden "Buy again" tab.
  UserRound,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { SubscriptionSignInState } from "./SubscriptionSignInState";
import { addToast } from "./Toast";
import { WidgetBuyAgainSheet } from "./WidgetBuyAgainSheet";
import {
  ManageSubscriptionSheet,
  purchasedProductToManageSheet,
} from "./manage-subscription";
import {
  discountPillPercent,
  formatSubscriptionDateForDisplay,
} from "./manage-subscription/formatters";
import {
  lineTotals,
  type SubscriptionLinePriceFields,
  subscriptionUnitPrices,
} from "./manage-subscription/subscription-pricing";
import { ReactivateSubscriptionDialog } from "./manage-subscription/ReactivateSubscriptionDialog";
import { SubscriptionActionPickerDialog } from "./manage-subscription/SubscriptionActionPickerDialog";
import { SubscriptionActionUnavailableDialog } from "./manage-subscription/SubscriptionActionUnavailableDialog";
import {
  describeIneligibility,
  getEligibleSubscriptions,
  isEligibleForDeepLink,
  type UnavailableReason,
} from "./manage-subscription/subscriptionEligibility";
import { toastSubscriptionApiError } from "./manage-subscription/subscription-errors";
import { MergeSubscriptionsBanner } from "./manage-subscription/MergeSubscriptionsBanner";
import { MergeSubscriptionsDialog } from "./manage-subscription/MergeSubscriptionsDialog";
import {
  getMergeableSubscriptions,
  getMergeableSubscriptionChips,
  getLargestMergeableBucket,
  currentShippingCostTotal,
  hasMergeableSubscriptions,
  mergeSavesShipping,
} from "./manage-subscription/merge-eligibility";
import { SizeUpSubscriptionsBanner } from "./manage-subscription/SizeUpSubscriptionsBanner";
import { SizeUpReviewDialog } from "./manage-subscription/SizeUpReviewDialog";
import { buildSizeUpBannerCopy } from "./manage-subscription/cancel-chat-cards";
import {
  computeSizeUpOfferForInput,
  getSizeUpCandidates,
  pickBestSizeUpOffer,
  resolveManageOfferBanners,
  type SizeUpComputeInput,
  type SizeUpOffer,
} from "./manage-subscription/size-up-eligibility";
import type { ManageSubscriptionInitialAction } from "./manage-subscription/types";
import { groupSubscriptions } from "shared/subscriptions";
import type { SubscriptionGroup } from "shared/subscriptions";
import {
  clearSubscriptionDeepLinkFromUrl,
  getCombineNotificationId,
  getSizeUpNotificationId,
  readMergeDeepLink,
  readSizeUpDeepLink,
  readSubscriptionDeepLink,
  type SubscriptionDeepLink,
  type SubscriptionDeepLinkAction,
} from "@/lib/subscriptionActionDeepLink";
import { cn } from "@/lib/utils";

interface BuyAgainPanelProps {
  isOpen: boolean;
  onClose: () => void;
  products: PurchasedProduct[] | undefined;
  productsLoading: boolean;
  shop?: string | null;
  userName?: string;
  /** Initial tab when both “Buy again” and “Manage subscriptions” have items. URL `so_panel_tab`, script `data-so-panel-tab`, or `window.__SOCIAL_OCEAN_BUY_AGAIN__` can set this at embed time. */
  defaultPanelTab?: DefaultPanelTab;
  /** Customer for checkout prefill when adding to cart */
  customer?: {
    shopify_customer_id?: string | number;
    email?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    shipping_address?: {
      first_name?: string;
      last_name?: string;
      address1?: string;
      address2?: string | null;
      city?: string;
      country?: string;
      province?: string;
      zip?: string;
      phone?: string | null;
    } | null;
  } | null;
  /** When true, panel was opened from the PDP subscription management link and should show the subscription empty state when no subscriptions exist. */
  openedFromSubscriptionLink?: boolean;
  /** How the panel was opened — used to tag login tracking events. */
  openSource?: WidgetOpenSource | null;
  /** Called after a successful email OTP sign-in from within the panel. */
  onSignInSuccess?: () => void;
  /** Called when the customer logs out from the panel footer. */
  onLogout?: () => void;
  /**
   * The feed loaded, but no customer record matched the identity we sent —
   * a sign-in against an email the store has never seen. Distinct from a
   * customer with nothing to show, and from a failed request.
   */
  accountNotFound?: boolean;
}

function ProductCard({
  product,
  groupProducts,
  onCustomize,
  onBuyAgain,
  trackingShop,
  onOpenManageSubscription,
}: {
  product: PurchasedProduct;
  groupProducts?: PurchasedProduct[];
  onCustomize: (productId: number) => void;
  onBuyAgain: (product: PurchasedProduct) => void;
  trackingShop?: string | null;
  onOpenManageSubscription?: (
    product: PurchasedProduct,
  ) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const setShippingAddressMutation = useMutation({
    mutationFn: ({
      subId,
      address,
    }: {
      subId: number;
      address: StayAiShippingAddressInput;
    }) => setShippingAddressStayAiSubscription(subId, address),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", product.id],
      });
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });
  const reactivateMutation = useMutation({
    mutationFn: (subscriptionId: number) =>
      reactivateStayAiSubscription(subscriptionId),
    onSuccess: (_data, subscriptionId) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract("subscription_reactivate", trackingShop, {
          productId: product.id,
          variantId: product.variant_id ?? undefined,
          subscription_contract_id: subscriptionId,
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", product.id],
      });
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });
  const reactivateBusy =
    reactivateMutation.isPending || setShippingAddressMutation.isPending;
  const subscriptionShippingAddress =
    product.subscription_shipping_address ?? null;
  const handleReactivateConfirm = async (payload: {
    address?: StayAiShippingAddressInput;
  }) => {
    const subId = product.shopify_subscription_id;
    if (subId == null) return;
    try {
      if (payload.address) {
        await setShippingAddressMutation.mutateAsync({
          subId,
          address: payload.address,
        });
      }
      reactivateMutation.mutate(subId);
    } catch {
      /* toast handled in onError */
    }
  };
  const hasVariant = product.variant_id != null;
  const isVariantHidden = product.is_variant_hidden;
  const previousQuantity =
    product.quantity > 0 ? Math.floor(product.quantity) : 0;
  const subscriptionStatusLower = product.subscription_status?.toLowerCase();
  const isSubscriptionCancelled = subscriptionStatusLower === "cancelled";
  const isFlexibleSubscription = product.subscription_is_flexible === true;
  const hasSubscription =
    subscriptionStatusLower === "active" ||
    subscriptionStatusLower === "paused" ||
    subscriptionStatusLower === "cancelled";

  const subscriptionPriceFields = purchasedProductSubscriptionPrices(product);
  const subscriptionLine = hasSubscription
    ? lineTotals(subscriptionPriceFields)
    : null;
  const subscriptionUnit = hasSubscription
    ? subscriptionUnitPrices(subscriptionPriceFields)
    : null;

  // One-time buy-again cards: variant price and welcome / code discounts.
  const basePriceSource = product.price;
  const productPriceNum =
    basePriceSource != null
      ? typeof basePriceSource === "number"
        ? basePriceSource
        : parseFloat(String(basePriceSource))
      : null;
  const productDiscountedPriceNum =
    product.price_after_discount != null
      ? typeof product.price_after_discount === "number"
        ? product.price_after_discount
        : parseFloat(String(product.price_after_discount))
      : null;

  const displayPrice = hasSubscription
    ? subscriptionUnit?.net
    : (productDiscountedPriceNum ?? productPriceNum);
  const originalPrice = hasSubscription
    ? subscriptionUnit?.original
    : productPriceNum;

  const savePercent = hasSubscription
    ? discountPillPercent(
        product.subscription_discounts,
        subscriptionLine?.original,
        subscriptionLine?.net,
      )
    : discountPillPercent(null, originalPrice, displayPrice);
  // Next delivery date for non-flexible active/paused subscriptions only.
  const nextDeliveryDate =
    !isFlexibleSubscription &&
    (subscriptionStatusLower === "active" ||
      subscriptionStatusLower === "paused")
      ? ((subscriptionStatusLower === "paused"
          ? (product.subscription_paused_until ??
            product.subscription_next_billing_date)
          : product.subscription_next_billing_date) ?? null)
      : null;

  const groupImages =
    groupProducts && groupProducts.length > 1
      ? groupProducts.slice(0, 4)
      : [];
  const groupCount = groupProducts?.length ?? 0;
  // Amount shown is the line TOTAL (unit × qty). Bundles sum every line.
  const amount = ((): { net: number | null; original: number | null } => {
    if (groupCount > 1 && groupProducts) {
      const totals = groupProducts
        .map(subscriptionLineTotals)
        .filter((t): t is { net: number; original: number } => t != null);
      if (totals.length === 0) return { net: null, original: null };
      return {
        net: totals.reduce((sum, t) => sum + t.net, 0),
        original: totals.reduce((sum, t) => sum + t.original, 0),
      };
    }
    if (hasSubscription && subscriptionLine) {
      return {
        net: subscriptionLine.net,
        original: subscriptionLine.original,
      };
    }
    return { net: displayPrice ?? null, original: originalPrice ?? null };
  })();
  const amountNet = amount.net;
  const amountOriginal = amount.original;
  // Cancelled subs: prefer the real cancellation date, fall back to the
  // contract's updated_at (last sync) when the backend has no cancelled_at.
  const cancelledOnDate = isSubscriptionCancelled
    ? (product.subscription_cancelled_at ??
      product.subscription_updated_at ??
      null)
    : null;

  return (
    <>
      <div className="tw:flex tw:gap-3 tw:py-3 ">
        <div className="tw:flex tw:w-1/3 tw:shrink-0 tw:flex-col tw:gap-1.5">
          <div className="tw:relative tw:aspect-square tw:bg-[#FDEDEE] tw:rounded-lg tw:overflow-hidden">
            {groupCount > 1 ? (
              // Multi-product subscription: a mosaic of the product images in the
              // same hero footprint as a single card, so the list stays even.
              <>
                <div className="tw:grid tw:grid-cols-2 tw:grid-rows-2 tw:gap-0.5 tw:w-full tw:h-full">
                  {groupImages.map((item, idx) => {
                    const span =
                      groupCount === 2 || (groupCount === 3 && idx === 0)
                        ? "tw:row-span-2"
                        : "";
                    const extra = groupCount - groupImages.length;
                    return (
                      <div
                        key={item.id}
                        className={`tw:relative tw:overflow-hidden tw:bg-[#FDEDEE] ${span}`}
                      >
                        <img
                          src={
                            item.image_url ??
                            "https://instantaccess.me/placeholder.png"
                          }
                          alt={item.title}
                          className="tw:w-full tw:h-full tw:object-cover"
                        />
                        {extra > 0 && idx === groupImages.length - 1 ? (
                          <div className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-[#523D33]/65 tw:text-sm tw:font-bold tw:text-white">
                            +{extra}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <span
                  className="tw:absolute tw:top-2 tw:left-2 tw:min-w-6 tw:h-6 tw:px-1.5 tw:rounded-md tw:bg-[#523D33] tw:text-white tw:text-xs tw:font-bold tw:flex tw:items-center tw:justify-center"
                  aria-label={`${groupCount} products`}
                >
                  {groupCount}
                </span>
              </>
            ) : (
              <>
                <img
                  src={
                    product.image_url ??
                    "https://instantaccess.me/placeholder.png"
                  }
                  alt={product.title}
                  className="tw:w-full tw:h-full tw:object-cover tw:rounded-lg"
                />
                {previousQuantity > 0 && (
                  <span
                    className="tw:absolute tw:top-2 tw:left-2 tw:min-w-6 tw:h-6 tw:px-1.5 tw:rounded-md tw:bg-[#523D33] tw:text-white tw:text-xs tw:font-bold tw:flex tw:items-center tw:justify-center"
                    aria-label={`Previously ordered ${previousQuantity}`}
                  >
                    {previousQuantity}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="tw:flex tw:flex-1 tw:flex-col tw:justify-between tw:min-w-0 tw:py-1">
          <div>
            {groupProducts && groupProducts.length > 1 ? (
              <p
                className="tw:font-semibold tw:text-sm tw:leading-snug tw:line-clamp-2"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {groupProducts.map((item) => item.title).join(", ")}
              </p>
            ) : (
              <>
                <p className="tw:font-semibold tw:text-base tw:line-clamp-2 tw:leading-tight">
                  {product.title}
                </p>
                <div className="tw:flex tw:items-center tw:gap-1.5 tw:mt-0.5 tw:flex-wrap">
                  {product.variants_count &&
                  product.variants_count > 1 &&
                  !hasSubscription ? (
                    <button
                      type="button"
                      onClick={() => onCustomize(product.id)}
                      className="tw:text-xs hover:tw:opacity-80 tw:cursor-pointer tw:bg-transparent tw:border-none tw:p-0 tw:text-[oklch(0.58_0.13_251.84)] hover:tw:text-blue-700 tw:text-left"
                    >
                      {product.variant_title ?? "Default"}
                    </button>
                  ) : (
                    <span
                      className="tw:text-xs"
                      style={{ color: "var(--so-text, #523D33)", opacity: 0.8 }}
                    >
                      {product.variant_title ?? "Default"}
                    </span>
                  )}
                </div>
              </>
            )}
            {nextDeliveryDate ? (
              <p
                className={`tw:text-xs ${groupCount > 1 ? "tw:mt-2" : "tw:mt-0.5"}`}
                style={{ color: "var(--so-text, #523D33)", opacity: 0.8 }}
              >
                <span className="tw:text-gray-400">Next delivery:</span>{" "}
                {formatSubscriptionDateForDisplay(nextDeliveryDate)}
              </p>
            ) : null}
            {cancelledOnDate ? (
              <p
                className={`tw:text-xs ${groupCount > 1 ? "tw:mt-2" : "tw:mt-0.5"}`}
                style={{ color: "var(--so-text, #523D33)", opacity: 0.8 }}
              >
                <span className="tw:text-gray-400">Cancelled on:</span>{" "}
                {formatSubscriptionDateForDisplay(cancelledOnDate)}
              </p>
            ) : null}
          </div>
          <div className="tw:flex tw:items-start tw:justify-end tw:gap-2 tw:flex-wrap">
            {amountNet != null && (
              <div className="tw:flex tw:items-center tw:gap-1.5 tw:flex-wrap tw:mr-auto">
                {!isVariantHidden &&
                  amountOriginal != null &&
                  amountOriginal > amountNet && (
                    <span className="tw:text-base tw:font-semibold tw:line-through tw:opacity-50">
                      ${amountOriginal.toFixed(2).replace(/\.00$/, "")}
                    </span>
                  )}
                <p
                  className={`tw:text-base tw:font-semibold ${isVariantHidden ? "tw:text-gray-400" : ""}`}
                >
                  ${amountNet.toFixed(2).replace(/\.00$/, "")}
                </p>
                {!isVariantHidden &&
                  groupCount <= 1 &&
                  savePercent != null &&
                  savePercent > 0 && (
                    <span className="tw:inline-flex tw:items-center tw:gap-1 tw:px-2 tw:py-1 tw:rounded tw:bg-[#FAC8CA] tw:text-[#523D33] tw:shrink-0 tw:text-sm tw:font-semibold tw:leading-none">
                      {savePercent}% OFF
                    </span>
                  )}
                {isVariantHidden && (
                  <span className="tw:text-gray-400">Out Of Stock</span>
                )}
              </div>
            )}
            {hasSubscription ? (
              isSubscriptionCancelled ? (
                <button
                  type="button"
                  disabled={
                    reactivateBusy || product.shopify_subscription_id == null
                  }
                  onClick={() => {
                    if (product.shopify_subscription_id == null) return;
                    reactivateMutation.reset();
                    setReactivateDialogOpen(true);
                  }}
                  className="tw:flex tw:items-center tw:justify-center tw:gap-1.5 tw:h-8 tw:px-4 tw:rounded-md tw:border tw:border-[#523D33] tw:bg-transparent tw:text-[#523D33] hover:tw:bg-[#523D33] hover:tw:text-[#FAC8CA] tw:transition-all tw:duration-300 tw:ease-in-out tw:cursor-pointer tw:text-sm tw:font-bold tw:uppercase tw:whitespace-nowrap disabled:tw:opacity-60 disabled:tw:pointer-events-none"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  {reactivateBusy ? (
                    <Loader2 className="tw:size-4 tw:animate-spin tw:shrink-0" />
                  ) : null}
                  Re-subscribe
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void onOpenManageSubscription?.(product)}
                  className={cn(
                    "tw:flex tw:items-center tw:justify-center tw:gap-1.5 tw:h-8 tw:px-4 tw:rounded-md tw:border tw:border-[#523D33] tw:bg-transparent tw:text-[#523D33] hover:tw:bg-[#523D33] hover:tw:text-[#FAC8CA] tw:transition-all tw:duration-300 tw:ease-in-out tw:cursor-pointer tw:text-sm tw:font-bold tw:uppercase tw:whitespace-nowrap",

                    product.subscription_is_flexible === true &&
                      "tw:bg-[#FAC8CA] tw:border-[#FAC8CA] tw:text-[#523D33] hover:tw:bg-[#FAC8CA]/80",
                  )}
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  {/* tw:bg-[#FAC8CA] tw:border-[#FAC8CA] tw:text-[#523D33] hover:tw:bg-[#FAC8CA]/80 tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px] */}
                  {product.subscription_is_flexible === true
                    ? "Get it now"
                    : "Manage"}
                </button>
              )
            ) : (
              <>
                {hasVariant && isVariantHidden && (
                  <button
                    type="button"
                    onClick={() => onCustomize(product.id)}
                    aria-label="Select option"
                    style={{ color: "var(--so-text, #523D33)" }}
                    className="tw:flex tw:font-bold tw:items-center tw:justify-center tw:gap-1.5 tw:border tw:border-[#523D33] tw:rounded-md tw:cursor-pointer tw:transition-all tw:duration-300 tw:ease-in-out tw:overflow-hidden tw:bg-transparent tw:text-[#523D33] tw:hover:bg-[#523D33] tw:hover:text-[#FAC8CA] tw:h-9 tw:px-4"
                  >
                    <span className="tw:text-sm tw:whitespace-nowrap">
                      CHANGE
                    </span>
                  </button>
                )}
                {hasVariant && !isVariantHidden && (
                  <button
                    type="button"
                    onClick={() => onBuyAgain(product)}
                    aria-label="Buy again"
                    className="tw:flex tw:items-center tw:justify-center tw:gap-1 tw:rounded-md tw:cursor-pointer tw:transition-opacity tw:overflow-hidden tw:bg-[#523D33] tw:text-[#FAC8CA] hover:tw:opacity-90 tw:h-9 tw:px-2 tw:border-0"
                  >
                    <Zap className="tw:size-4 tw:fill-current" />
                    <span className="tw:text-sm tw:font-semibold tw:whitespace-nowrap">
                      Buy Again
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <ReactivateSubscriptionDialog
        open={reactivateDialogOpen}
        onOpenChange={(nextOpen) => {
          setReactivateDialogOpen(nextOpen);
          if (!nextOpen && reactivateMutation.isSuccess) {
            reactivateMutation.reset();
            setShippingAddressMutation.reset();
          }
        }}
        productTitle={product.title}
        initialAddress={subscriptionShippingAddress}
        onConfirm={handleReactivateConfirm}
        confirmPending={reactivateBusy}
        confirmSuccess={reactivateMutation.isSuccess}
      />
    </>
  );
}

function purchasedProductSubscriptionPrices(
  p: PurchasedProduct,
): SubscriptionLinePriceFields & { quantity?: number } {
  return {
    quantity: p.quantity,
    price: p.price,
    order_line_item_price: p.order_line_item_price,
    subscription_unit_price: p.subscription_unit_price,
    subscription_one_time_price: p.subscription_one_time_price,
    subscription_discounted_price: p.subscription_discounted_price,
    subscription_discounts: p.subscription_discounts,
  };
}

/**
 * Per-line subscription total (unit price × quantity), net and original.
 * Mirrors the single-card price logic so a bundle can sum its lines.
 */
function subscriptionLineTotals(
  p: PurchasedProduct,
): { net: number; original: number } | null {
  const status = p.subscription_status?.trim().toLowerCase() ?? "";
  const hasSub =
    status === "active" || status === "paused" || status === "cancelled";
  if (!hasSub) return null;
  const { net, original } = lineTotals(purchasedProductSubscriptionPrices(p));
  if (net == null) return null;
  return { net, original: original ?? net };
}

/**
 * True → product goes in Manage subscriptions tab. False → Buy again tab.
 * Cancelled subs with a usable variant stay in Manage so the Reactivate button
 * shows; cancelled with hidden/missing variant falls through to Buy again so
 * the customer picks a fresh variant.
 */
export function hasSubscriptionStatus(product: PurchasedProduct): boolean {
  const s = product.subscription_status?.trim().toLowerCase();
  if (!s) return false;
  if (s === "cancelled" && !canReactivate(product)) return false;
  return true;
}

function canReactivate(product: PurchasedProduct): boolean {
  return (
    product.variant_id != null &&
    !product.is_variant_hidden &&
    product.shopify_subscription_id != null
  );
}

/**
 * Build-time flag for the subscription-merge banner/dialog. Defaults OFF so the
 * feature can ship dark and be enabled deliberately with a widget build that
 * sets VITE_SUBSCRIPTION_MERGE=true. The banner is the feature's only entry
 * point, so gating it gates the whole flow.
 */
const SUBSCRIPTION_MERGE_ENABLED =
  import.meta.env.VITE_SUBSCRIPTION_MERGE === "true";

/**
 * Build-time flag for the Rose Nectar size-up banner/dialog. Defaults OFF
 * (same dark-ship pattern as merge).
 */
const SUBSCRIPTION_SIZE_UP_ENABLED =
  import.meta.env.VITE_SUBSCRIPTION_SIZE_UP === "true";

export function BuyAgainPanel({
  isOpen,
  onClose,
  products,
  productsLoading,
  shop,
  userName,
  defaultPanelTab,
  customer: listCustomer,
  openSource,
  onSignInSuccess,
  onLogout,
  accountNotFound = false,
}: BuyAgainPanelProps) {
  const isIdentified = useMemo(() => hasCustomerId(), [products]);
  // The address to name the account by. The backend reports it for a known
  // customer; in token mode it falls back to whatever was signed in with.
  const signedInEmail = listCustomer?.email ?? getStoredEmail();
  const hasNoAccount = isIdentified && !productsLoading && accountNotFound;
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [panelTab, setPanelTab] = useState<"buy-again" | "manage">(
    // TEMP: "Buy again" tab is hidden (see the commented-out tab button in the
    // tablist). Default to "manage" so the only visible tab matches the view.
    // Restore to `defaultPanelTab ?? "buy-again"` when the tab is re-enabled.
    () => defaultPanelTab ?? "manage",
  );
  const [manageSubscriptionProduct, setManageSubscriptionProduct] =
    useState<PurchasedProduct | null>(null);
  const [manageSheetInitialAction, setManageSheetInitialAction] = useState<
    ManageSubscriptionInitialAction | undefined
  >(undefined);
  const [manageSheetSessionKey, setManageSheetSessionKey] = useState(0);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  // Session-scoped: hide the merge banner once dismissed or after a request is
  // sent (nothing changes server-side, so eligibility would otherwise persist).
  const [mergeBannerSuppressed, setMergeBannerSuppressed] = useState(false);
  const [sizeUpDialogOpen, setSizeUpDialogOpen] = useState(false);
  // When the deep link targets a specific sub, override the banner's "best"
  // offer so that subscription opens even if another saves more.
  const [sizeUpDialogOfferOverride, setSizeUpDialogOfferOverride] =
    useState<SizeUpOffer | null>(null);
  const [sizeUpBannerSuppressed, setSizeUpBannerSuppressed] = useState(false);

  const [deepLink] = useState<SubscriptionDeepLink | null>(() =>
    readSubscriptionDeepLink(),
  );
  const [deepLinkResolved, setDeepLinkResolved] = useState(false);
  const [mergeDeepLink] = useState(() => readMergeDeepLink());
  const [mergeDeepLinkResolved, setMergeDeepLinkResolved] = useState(false);
  const [sizeUpDeepLink] = useState(() => readSizeUpDeepLink());
  const [sizeUpDeepLinkResolved, setSizeUpDeepLinkResolved] = useState(false);
  // Captured at first render, before any effect (incl. the merge deep-link
  // effect below) can run `clearSubscriptionDeepLinkFromUrl()` and strip
  // `so_combine_nid` / `so_sizeup_nid` from the URL. A `useEffect` read would
  // race that strip.
  const [combineNid] = useState(() => getCombineNotificationId());
  const [sizeUpNid] = useState(() => getSizeUpNotificationId());
  const [pickerAction, setPickerAction] =
    useState<SubscriptionDeepLinkAction | null>(null);
  const [pickerSubscriptions, setPickerSubscriptions] = useState<
    PurchasedProduct[]
  >([]);
  const [unavailable, setUnavailable] = useState<{
    action: SubscriptionDeepLinkAction;
    reason: UnavailableReason;
    productTitle?: string | null;
    date?: string | null;
  } | null>(null);

  const manageSheetProduct = useMemo(() => {
    if (manageSubscriptionProduct == null) return null;
    // Prefer the live product from the refreshed list so the open manage sheet
    // reflects actions taken during the flow (e.g. delay → new next delivery date),
    // falling back to the snapshot if it's no longer in the list.
    const live =
      (products ?? []).find(
        (p) =>
          p.id === manageSubscriptionProduct.id &&
          p.shopify_subscription_id ===
            manageSubscriptionProduct.shopify_subscription_id,
      ) ?? manageSubscriptionProduct;
    return purchasedProductToManageSheet(live);
  }, [manageSubscriptionProduct, products]);

  useEffect(() => {
    if (manageSubscriptionProduct != null && manageSheetProduct == null) {
      setManageSubscriptionProduct(null);
    }
  }, [manageSubscriptionProduct, manageSheetProduct]);

  async function openManageSubscriptionForProduct(
    p: PurchasedProduct,
    opts?: { initialAction?: ManageSubscriptionInitialAction },
  ) {
    if (p.shopify_subscription_id != null) {
      setManageSheetSessionKey((k) => k + 1);
      setManageSheetInitialAction(
        opts?.initialAction ??
          (p.subscription_is_flexible === true ? "get-it-now" : undefined),
      );
      setManageSubscriptionProduct(p);
      return;
    }
    try {
      const detail = (await fetchPurchasedProductDetail(
        p.id,
      )) as PurchasedProductDetail | null;
      if (detail?.shopify_subscription_id != null) {
        const isFlexible =
          (detail.subscription_is_flexible ?? p.subscription_is_flexible) ===
          true;
        setManageSheetSessionKey((k) => k + 1);
        setManageSheetInitialAction(
          opts?.initialAction ?? (isFlexible ? "get-it-now" : undefined),
        );
        setManageSubscriptionProduct({
          ...p,
          shopify_subscription_id: detail.shopify_subscription_id,
          subscription_status:
            detail.subscription_status ?? p.subscription_status,
          subscription_next_billing_date:
            detail.subscription_next_billing_date ??
            p.subscription_next_billing_date,
          subscription_last_charge_date:
            detail.subscription_last_charge_date ??
            p.subscription_last_charge_date,
          can_skip_next_order:
            detail.can_skip_next_order ?? p.can_skip_next_order,
          subscription_max_reschedule_date:
            detail.subscription_max_reschedule_date ??
            p.subscription_max_reschedule_date,
          subscription_paused_until:
            detail.subscription_paused_until ?? p.subscription_paused_until,
          paused_until: detail.paused_until ?? p.paused_until,
          subscription_cancellation_requested_at:
            detail.subscription_cancellation_requested_at ??
            p.subscription_cancellation_requested_at,
          subscription_shipping_address:
            detail.subscription_shipping_address ??
            p.subscription_shipping_address,
          subscription_is_flexible:
            detail.subscription_is_flexible ?? p.subscription_is_flexible,
          subscription_conversion_fee_paid:
            detail.subscription_conversion_fee_paid ??
            p.subscription_conversion_fee_paid,
          subscription_cancel_scheduled_at:
            detail.subscription_cancel_scheduled_at ??
            p.subscription_cancel_scheduled_at,
        });
        return;
      }
    } catch {
      /* network / parse */
    }
    addToast({
      message:
        "We couldn't load subscription details. Check your connection or try again.",
      color: "danger",
    });
  }

  const { buyAgainProducts, manageSubscriptionProducts } = useMemo(() => {
    const list = products ?? [];
    return {
      buyAgainProducts: list.filter((p) => !hasSubscriptionStatus(p)),
      manageSubscriptionProducts: list.filter((p) => hasSubscriptionStatus(p)),
    };
  }, [products]);

  const manageSheetProducts = useMemo(() => {
    const subId = manageSubscriptionProduct?.shopify_subscription_id;
    if (subId == null) {
      return manageSheetProduct ? [manageSheetProduct] : [];
    }
    const groupRows = manageSubscriptionProducts.filter(
      (p) => p.shopify_subscription_id === subId,
    );
    return groupRows
      .map(purchasedProductToManageSheet)
      .filter((p): p is NonNullable<typeof p> => p != null);
  }, [manageSubscriptionProduct, manageSheetProduct, manageSubscriptionProducts]);

  useEffect(() => {
    if (!deepLink || deepLinkResolved) return;
    if (productsLoading || !products) return;
    const subs = manageSubscriptionProducts;
    const targetId = deepLink.subscriptionId;
    const initialAction: ManageSubscriptionInitialAction = deepLink.action;

    if (targetId != null) {
      const targetIdStr = String(targetId);
      const match = subs.find(
        (p) =>
          p.shopify_subscription_id != null &&
          String(p.shopify_subscription_id) === targetIdStr,
      );
      if (!match) {
        setUnavailable({
          action: deepLink.action,
          reason: "subscription-not-found",
        });
      } else if (isEligibleForDeepLink(match, deepLink.action)) {
        void openManageSubscriptionForProduct(match, { initialAction });
      } else {
        setUnavailable({
          action: deepLink.action,
          reason: describeIneligibility(match, deepLink.action),
          productTitle: match.title,
          date:
            match.subscription_paused_until ??
            match.subscription_next_billing_date ??
            null,
        });
      }
    } else {
      const eligible = getEligibleSubscriptions(subs, deepLink.action);
      if (eligible.length === 0) {
        setUnavailable({
          action: deepLink.action,
          reason: "no-active-subscriptions",
        });
      } else if (eligible.length === 1) {
        void openManageSubscriptionForProduct(eligible[0], { initialAction });
      } else {
        setPickerAction(deepLink.action);
        setPickerSubscriptions(eligible);
      }
    }

    clearSubscriptionDeepLinkFromUrl();
    setDeepLinkResolved(true);
  }, [
    deepLink,
    deepLinkResolved,
    productsLoading,
    products,
    manageSubscriptionProducts,
  ]);

  const effectiveShop = shop ?? getShopForTracking(products ?? []);

  // Arrived from the "eligible to combine" email: stamp the session so every
  // combine event downstream is attributable to it, and record the landing —
  // this is our click-through signal, since Klaviyo's own click telemetry is
  // not mirrored into PostHog. The stamp is set before the landed event fires
  // so the landed event itself carries the id.
  const combineNidTrackedRef = useRef(false);
  useEffect(() => {
    if (!combineNid || combineNidTrackedRef.current || !effectiveShop) return;
    combineNidTrackedRef.current = true;
    setCombineNotificationId(combineNid);
    trackBuyAgainWidgetInteract("subscription_merge_email_landed", effectiveShop);
  }, [combineNid, effectiveShop]);

  // Same pattern for size-up email landings (`so_sizeup_nid`).
  const sizeUpNidTrackedRef = useRef(false);
  useEffect(() => {
    if (!sizeUpNid || sizeUpNidTrackedRef.current || !effectiveShop) return;
    sizeUpNidTrackedRef.current = true;
    setSizeUpNotificationId(sizeUpNid);
    trackBuyAgainWidgetInteract("subscription_size_up_email_landed", effectiveShop);
  }, [sizeUpNid, effectiveShop]);

  const buyAgainCount = buyAgainProducts.length;
  const manageCount = manageSubscriptionProducts.length;

  useEffect(() => {
    if (!isOpen || productsLoading || !products?.length) return;
    // TEMP: while the "Buy again" tab is hidden, always stay on "manage" so the
    // panel never auto-switches to the (hidden) buy-again view. Restore the
    // count-based selection below when re-enabling the tab.
    setPanelTab("manage");
    // setPanelTab((t) => {
    //   if (buyAgainCount > 0 && manageCount === 0) return "buy-again";
    //   if (manageCount > 0 && buyAgainCount === 0) return "manage";
    //   if (buyAgainCount > 0 && manageCount > 0) {
    //     if (t === "buy-again" && buyAgainCount > 0) return t;
    //     if (t === "manage" && manageCount > 0) return t;
    //     const preferred = defaultPanelTab ?? "buy-again";
    //     if (preferred === "manage" && manageCount > 0) return "manage";
    //     if (preferred === "buy-again" && buyAgainCount > 0) return "buy-again";
    //     return buyAgainCount > 0 ? "buy-again" : "manage";
    //   }
    //   return t;
    // });
  }, [
    isOpen,
    productsLoading,
    products?.length,
    buyAgainCount,
    manageCount,
    defaultPanelTab,
  ]);

  const handleCustomize = (productId: number) => {
    if (effectiveShop)
      trackBuyAgainWidgetInteract("customize", effectiveShop, { productId });
    setSelectedProductId(productId);
    setSheetOpen(true);
  };

  // Lock body scroll when panel is open (prevents background scroll on mobile).
  // Re-applies when sheetOpen becomes false because Base UI's Sheet restores body on close.
  const scrollYRef = useRef(0);
  const applyLock = (captureScroll = true) => {
    if (captureScroll) scrollYRef.current = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  };
  const removeLock = () => {
    const y = scrollYRef.current;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, y);
  };
  useEffect(() => {
    if (!isOpen) return;
    applyLock();
    return removeLock;
  }, [isOpen]);
  // Re-apply lock when nested sheet closes (Base UI restores body, breaking our lock)
  useEffect(() => {
    if (isOpen && !sheetOpen) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => applyLock(false));
      });
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen, sheetOpen]);

  const visibleProducts =
    panelTab === "buy-again" ? buyAgainProducts : manageSubscriptionProducts;

  // Merge: active, same-frequency-eligible subscriptions to choose from, and
  // whether any frequency bucket has ≥2 (i.e. a merge is actually possible).
  const mergeableSubscriptions = useMemo(
    () => getMergeableSubscriptions(manageSubscriptionProducts),
    [manageSubscriptionProducts],
  );
  const canMerge = useMemo(
    () => hasMergeableSubscriptions(manageSubscriptionProducts),
    [manageSubscriptionProducts],
  );
  const mergeChips = useMemo(
    () => getMergeableSubscriptionChips(manageSubscriptionProducts),
    [manageSubscriptionProducts],
  );
  const mergeSavesShippingCost = useMemo(
    () => mergeSavesShipping(manageSubscriptionProducts),
    [manageSubscriptionProducts],
  );
  // The group the banner advertises (largest same-frequency bucket) — used to
  // quote the merged shipping and derive the real dollar saving.
  const mergeLargestBucket = useMemo(
    () => getLargestMergeableBucket(manageSubscriptionProducts),
    [manageSubscriptionProducts],
  );
  const mergeQuoteIds = useMemo(
    () => mergeLargestBucket.map((s) => s.subscriptionId),
    [mergeLargestBucket],
  );

  // Size-up: prefilter Rose Nectar single-line ladder candidates, fetch product
  // detail + target shipping, then pick the highest savingPerMonth offer.
  const sizeUpCandidates = useMemo(
    () => getSizeUpCandidates(manageSubscriptionProducts),
    [manageSubscriptionProducts],
  );
  const sizeUpFeatureActive =
    SUBSCRIPTION_SIZE_UP_ENABLED &&
    isIdentified &&
    !sizeUpBannerSuppressed &&
    (panelTab === "manage" || sizeUpDeepLink != null);
  const sizeUpCandidateKey = sizeUpCandidates
    .map((c) => `${c.subscriptionId}:${c.product.id}`)
    .join(",");

  const sizeUpDetailsQuery = useQuery({
    queryKey: ["size-up-product-details", sizeUpCandidateKey],
    queryFn: async () => {
      const rows = await Promise.all(
        sizeUpCandidates.map(async (candidate) => {
          const detail = (await fetchPurchasedProductDetail(
            candidate.product.id,
          )) as PurchasedProductDetail | null;
          return { candidate, detail };
        }),
      );
      return rows;
    },
    enabled: sizeUpFeatureActive && sizeUpCandidates.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Structural preview picks (product-only, then optimistic $0 target ship when
  // the customer currently pays a fee) — tells us which target variant to quote.
  const sizeUpPreviewTargets = useMemo(() => {
    const rows = sizeUpDetailsQuery.data ?? [];
    const out: {
      subscriptionId: number;
      variantId: number;
      quantity: number;
    }[] = [];
    for (const { candidate, detail } of rows) {
      if (!detail?.variants?.length) continue;
      const baseInput: SizeUpComputeInput = {
        candidate,
        variants: detail.variants,
        currentVariantId: detail.variant_id ?? candidate.product.variant_id,
        currentVariantTitle:
          detail.variants.find((v) => v.shopify_id === detail.variant_id)
            ?.title ??
          candidate.product.variant_title ??
          null,
        targetShippingCost: null,
      };
      let offer = computeSizeUpOfferForInput(baseInput);
      if (
        !offer &&
        (candidate.product.subscription_shipping_price ?? 0) > 0
      ) {
        offer = computeSizeUpOfferForInput({
          ...baseInput,
          targetShippingCost: 0,
        });
      }
      if (!offer) continue;
      out.push({
        subscriptionId: candidate.subscriptionId,
        variantId: offer.bundle.targetVariantId,
        quantity: offer.bundle.quantity,
      });
    }
    return out;
  }, [sizeUpDetailsQuery.data]);

  const sizeUpShippingKey = sizeUpPreviewTargets
    .map((t) => `${t.subscriptionId}:${t.variantId}:${t.quantity}`)
    .join(",");

  const sizeUpShippingQuery = useQuery({
    queryKey: ["size-up-shipping-preview", sizeUpShippingKey],
    queryFn: async () => {
      const entries = await Promise.all(
        sizeUpPreviewTargets.map(async (t) => {
          const preview = await previewSubscriptionShipping(t.subscriptionId, {
            variantId: t.variantId,
            quantity: t.quantity,
          });
          return [t.subscriptionId, preview.shippingPrice] as const;
        }),
      );
      return Object.fromEntries(entries) as Record<number, number | null>;
    },
    enabled: sizeUpPreviewTargets.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Wait for shipping preview before showing savings — otherwise product-only
  // math (~$11) flashes, then jumps when shipping is folded in (~$8). Optimistic
  // $0 target shipping is only used above to decide whether a preview is worth
  // fetching, never for customer-facing figures.
  const sizeUpShippingSettled =
    sizeUpPreviewTargets.length === 0 || sizeUpShippingQuery.isFetched;

  const bestSizeUpOffer = useMemo((): SizeUpOffer | null => {
    if (!sizeUpShippingSettled) return null;
    const rows = sizeUpDetailsQuery.data ?? [];
    if (rows.length === 0) return null;
    const inputs: SizeUpComputeInput[] = [];
    for (const { candidate, detail } of rows) {
      if (!detail?.variants?.length) continue;
      const fetchedShip = sizeUpShippingQuery.data?.[candidate.subscriptionId];
      inputs.push({
        candidate,
        variants: detail.variants,
        currentVariantId: detail.variant_id ?? candidate.product.variant_id,
        currentVariantTitle:
          detail.variants.find((v) => v.shopify_id === detail.variant_id)
            ?.title ??
          candidate.product.variant_title ??
          null,
        // Only real preview (or null if unavailable) — never optimistic $0.
        targetShippingCost: fetchedShip !== undefined ? fetchedShip : null,
      });
    }
    return pickBestSizeUpOffer(inputs);
  }, [
    sizeUpShippingSettled,
    sizeUpDetailsQuery.data,
    sizeUpShippingQuery.data,
  ]);

  // Offer for a specific subscription (deep link) — may differ from "best".
  const sizeUpOfferBySubscriptionId = useMemo(() => {
    const map = new Map<number, SizeUpOffer>();
    if (!sizeUpShippingSettled) return map;
    const rows = sizeUpDetailsQuery.data ?? [];
    for (const { candidate, detail } of rows) {
      if (!detail?.variants?.length) continue;
      const fetchedShip = sizeUpShippingQuery.data?.[candidate.subscriptionId];
      const offer = computeSizeUpOfferForInput({
        candidate,
        variants: detail.variants,
        currentVariantId: detail.variant_id ?? candidate.product.variant_id,
        currentVariantTitle:
          detail.variants.find((v) => v.shopify_id === detail.variant_id)
            ?.title ??
          candidate.product.variant_title ??
          null,
        targetShippingCost: fetchedShip !== undefined ? fetchedShip : null,
      });
      if (offer) map.set(candidate.subscriptionId, offer);
    }
    return map;
  }, [
    sizeUpShippingSettled,
    sizeUpDetailsQuery.data,
    sizeUpShippingQuery.data,
  ]);

  const sizeUpEligible = bestSizeUpOffer != null;
  const sizeUpBannerCopy = useMemo(
    () =>
      bestSizeUpOffer
        ? buildSizeUpBannerCopy(bestSizeUpOffer.bundle)
        : null,
    [bestSizeUpOffer],
  );
  const mergeWouldShow =
    SUBSCRIPTION_MERGE_ENABLED &&
    panelTab === "manage" &&
    isIdentified &&
    canMerge &&
    !mergeBannerSuppressed;
  const { showSizeUpBanner, showMergeBanner } = resolveManageOfferBanners({
    sizeUpEligible:
      SUBSCRIPTION_SIZE_UP_ENABLED &&
      panelTab === "manage" &&
      isIdentified &&
      sizeUpEligible,
    sizeUpSuppressed: sizeUpBannerSuppressed,
    mergeEligible: mergeWouldShow,
    mergeSuppressed: false, // already folded into mergeWouldShow
  });

  // Real merged-shipping quote for the advertised group. Only fires when the
  // banner is shown and there's a shipping fee that merging could actually save.
  const mergePreviewQuery = useQuery({
    queryKey: ["merge-shipping-preview", mergeQuoteIds],
    queryFn: () => previewSubscriptionMergeShipping(mergeQuoteIds),
    enabled:
      showMergeBanner && mergeSavesShippingCost && mergeQuoteIds.length >= 2,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const mergeShippingSavingsAmount = useMemo(() => {
    const merged = mergePreviewQuery.data?.mergedShippingPrice;
    if (merged == null) return null;
    const savings = currentShippingCostTotal(mergeLargestBucket) - merged;
    return savings > 0.005 ? Math.round(savings * 100) / 100 : null;
  }, [mergePreviewQuery.data, mergeLargestBucket]);

  // Fire the "shown" event once per session when the banner first appears.
  const mergeBannerTrackedRef = useRef(false);
  useEffect(() => {
    if (showMergeBanner && !mergeBannerTrackedRef.current && effectiveShop) {
      mergeBannerTrackedRef.current = true;
      trackBuyAgainWidgetInteract("subscription_merge_banner_shown", effectiveShop);
    }
  }, [showMergeBanner, effectiveShop]);

  const sizeUpBannerTrackedRef = useRef(false);
  useEffect(() => {
    if (showSizeUpBanner && !sizeUpBannerTrackedRef.current && effectiveShop) {
      sizeUpBannerTrackedRef.current = true;
      trackBuyAgainWidgetInteract(
        "subscription_size_up_banner_shown",
        effectiveShop,
        {
          subscription_contract_id:
            bestSizeUpOffer?.candidate.subscriptionId,
          saving_per_month: bestSizeUpOffer?.bundle.savingPerMonth,
        },
      );
    }
  }, [showSizeUpBanner, effectiveShop, bestSizeUpOffer]);

  // Auto-open the merge dialog from `?so_action=merge-subscriptions`. Waits for
  // products to load so `canMerge` is real; if the customer has nothing to merge
  // we simply strip the param and no-op instead of opening an empty dialog.
  // Deep links still open even when the size-up banner won organic exclusivity.
  useEffect(() => {
    if (!mergeDeepLink || mergeDeepLinkResolved) return;
    if (productsLoading || !products) return;
    if (canMerge && !mergeBannerSuppressed) {
      setPanelTab("manage");
      setMergeDialogOpen(true);
      if (effectiveShop) {
        trackBuyAgainWidgetInteract("subscription_merge_start", effectiveShop, {
          source: "deep_link",
        });
      }
    }
    clearSubscriptionDeepLinkFromUrl();
    setMergeDeepLinkResolved(true);
  }, [
    mergeDeepLink,
    mergeDeepLinkResolved,
    productsLoading,
    products,
    canMerge,
    mergeBannerSuppressed,
    effectiveShop,
  ]);

  // Auto-open the size-up review dialog from `?so_action=size-up-bundle`.
  // Waits for detail/shipping so eligibility is real; no-ops when ineligible.
  // Deep links still open even when the merge banner would otherwise show
  // (organic exclusivity only suppresses the other banner, not deep links).
  const sizeUpDeepLinkOffer = sizeUpDeepLink
    ? (sizeUpOfferBySubscriptionId.get(sizeUpDeepLink.subscriptionId) ?? null)
    : null;
  useEffect(() => {
    if (!sizeUpDeepLink || sizeUpDeepLinkResolved) return;
    if (productsLoading || !products) return;
    if (!SUBSCRIPTION_SIZE_UP_ENABLED) {
      clearSubscriptionDeepLinkFromUrl();
      setSizeUpDeepLinkResolved(true);
      return;
    }
    // Details / shipping still settling — wait for a real offer (or none).
    if (
      sizeUpCandidates.length > 0 &&
      (sizeUpDetailsQuery.isLoading ||
        (sizeUpDetailsQuery.isFetching && !sizeUpDetailsQuery.data) ||
        !sizeUpShippingSettled)
    ) {
      return;
    }
    if (sizeUpDeepLinkOffer && !sizeUpBannerSuppressed) {
      setPanelTab("manage");
      setSizeUpDialogOfferOverride(sizeUpDeepLinkOffer);
      setSizeUpDialogOpen(true);
      if (effectiveShop) {
        trackBuyAgainWidgetInteract("subscription_size_up_start", effectiveShop, {
          source: "deep_link",
          subscription_contract_id:
            sizeUpDeepLinkOffer.candidate.subscriptionId,
          saving_per_month: sizeUpDeepLinkOffer.bundle.savingPerMonth,
        });
      }
    }
    clearSubscriptionDeepLinkFromUrl();
    setSizeUpDeepLinkResolved(true);
  }, [
    sizeUpDeepLink,
    sizeUpDeepLinkResolved,
    productsLoading,
    products,
    sizeUpCandidates.length,
    sizeUpDetailsQuery.isLoading,
    sizeUpDetailsQuery.isFetching,
    sizeUpDetailsQuery.data,
    sizeUpShippingSettled,
    sizeUpDeepLinkOffer,
    sizeUpBannerSuppressed,
    effectiveShop,
  ]);

  const sizeUpDialogOffer =
    sizeUpDialogOfferOverride ?? (sizeUpDialogOpen ? bestSizeUpOffer : null);

  const customerFullName =
    listCustomer?.full_name?.trim() ||
    [listCustomer?.first_name, listCustomer?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    null;

  const hasProducts = products != null && products.length > 0;
  const showTabs = !productsLoading && (hasProducts || !WELCOME_OFFER_ENABLED);
  // Use the marketing header in the empty state; switch to the personal
  // greeting once the user has products to look at.
  const showWelcomeHeader = !hasProducts && WELCOME_OFFER_ENABLED;

  const welcomeCodeApplied = isWelcomeCodeApplied();

  const handleWelcomeOfferClick = () => {
    if (effectiveShop) {
      trackBuyAgainWidgetInteract("welcome_offer_click", effectiveShop);
    }
    markWelcomeOfferApplyPending();
    const path = window.location.pathname;
    const isProductPage = /^\/products\//.test(path);
    const url = isProductPage
      ? `/discount/instant-access?redirect=${encodeURIComponent(path + window.location.search)}`
      : "/discount/instant-access";
    window.location.assign(url);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop: click outside to close */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close panel"
        className="tw:fixed tw:inset-0 tw:z-40 tw:bg-black/20 tw:border-0 tw:cursor-default tw:backdrop-blur-[2px]"
      />
      {/* Mobile: full-screen modal. Desktop (md+): floating bottom-right panel. */}
      <div className="tw:fixed tw:top-0 tw:left-0 tw:bottom-0 tw:right-0 tw:z-50 tw:w-full tw:md:top-auto tw:md:left-auto tw:md:max-w-sm">
        <div className="tw:bg-white tw:h-full tw:md:h-auto tw:md:rounded-t-xl tw:md:shadow-[0_4px_24px_rgba(0,0,0,0.12)] tw:md:border tw:md:border-gray-100 tw:overflow-hidden tw:flex tw:flex-col tw:md:max-h-[90vh] tw:relative">
          {/* Header */}
          <div
            className={
              showWelcomeHeader
                ? "tw:flex tw:items-start tw:justify-between tw:px-4 tw:pt-3 tw:pb-3 tw:border-b tw:border-gray-100"
                : "tw:flex tw:items-center tw:justify-between tw:px-4 tw:pt-3 tw:pb-2"
            }
          >
            {showWelcomeHeader ? (
              <div className="tw:flex tw:items-start tw:gap-2">
                <span className="tw:text-2xl tw:leading-none">⚡️</span>
                <div className="tw:flex tw:flex-col tw:gap-0.5">
                  <span
                    className="tw:font-semibold tw:text-base tw:leading-tight"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    Instant Access
                  </span>
                  <span
                    className="tw:text-sm tw:font-normal tw:opacity-70 tw:leading-tight"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    to your products, subscriptions & perks
                  </span>
                </div>
              </div>
            ) : (
              <span
                className="tw:font-semibold tw:text-base"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {userName ? `⚡️ Hi, ${userName}` : "⚡️ Hi there!"}
              </span>
            )}
            <button
              onClick={onClose}
              className="tw:cursor-pointer tw:transition-colors tw:bg-transparent tw:border-none tw:p-0 hover:tw:opacity-70"
              style={{ color: "var(--so-text, #523D33)" }}
              aria-label="Close"
            >
              <X className="tw:size-4" />
            </button>
          </div>

          {showTabs && (
            <div className="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:pb-2">
              <div
                className="tw:flex tw:items-center tw:gap-2"
                role="tablist"
                aria-label="Panel sections"
              >
                {/* TEMPORARILY HIDDEN: "Buy again" / "All" tab. Keep the code —
                    re-enable by uncommenting and restoring the default panelTab
                    to "buy-again" (see useState above). */}
                {/* <button
                  type="button"
                  role="tab"
                  id="buy-again-tab"
                  aria-selected={panelTab === "buy-again"}
                  onClick={() => setPanelTab("buy-again")}
                  className={[
                    "tw:flex-1 tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:py-2.5 tw:px-4 tw:rounded-full tw:text-sm tw:font-semibold tw:transition-colors tw:border tw:cursor-pointer tw:whitespace-nowrap",
                    panelTab === "buy-again"
                      ? "tw:bg-[#FDEDEE] tw:border-transparent"
                      : "tw:bg-white tw:border-gray-200 hover:tw:bg-gray-50",
                  ].join(" ")}
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  <ShoppingBag className="tw:size-4" />
                  All
                </button> */}
                <button
                  type="button"
                  role="tab"
                  id="manage-subscriptions-tab"
                  aria-selected={panelTab === "manage"}
                  onClick={() => setPanelTab("manage")}
                  className={[
                    "tw:flex-1 tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:py-2.5 tw:px-4 tw:rounded-full tw:text-sm tw:font-semibold tw:transition-colors tw:border tw:cursor-pointer tw:whitespace-nowrap",
                    panelTab === "manage"
                      ? "tw:bg-[#FDEDEE] tw:border-transparent"
                      : "tw:bg-white tw:border-gray-200 hover:tw:bg-gray-50",
                  ].join(" ")}
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  <RefreshCw className="tw:size-4" />
                  Your subscriptions
                </button>
              </div>
            </div>
          )}

          <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
            {productsLoading ? (
              <div className="tw:flex tw:items-center tw:justify-center tw:py-10 tw:min-h-78">
                <Loader2
                  className="tw:size-10 tw:animate-spin"
                  style={{ color: "var(--so-text, #523D33)" }}
                />
              </div>
            ) : (!products || products.length === 0) &&
              WELCOME_OFFER_ENABLED ? (
              <div
                className="tw:flex tw:flex-col tw:py-6 tw:px-4 tw:min-h-109"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {!isIdentified && (
                  <>
                    <div className="tw:flex tw:items-center tw:gap-4">
                      <div
                        className="tw:w-16 tw:h-16 tw:rounded-full tw:shrink-0 tw:flex tw:items-center tw:justify-center"
                        style={{ backgroundColor: "#FAC8CA66" }}
                      >
                        <UserRound
                          className="tw:size-9"
                          strokeWidth={1.25}
                          style={{ color: "#000" }}
                        />
                      </div>
                      <div className="tw:flex tw:flex-col tw:gap-2 tw:flex-1 tw:min-w-0">
                        <span className="tw:text-base tw:font-semibold">
                          Already a customer?
                        </span>
                        <SubscriptionSignInState
                          onSignInSuccess={() => {
                            onSignInSuccess?.();
                            // Existing customers who sign in from the welcome
                            // empty state get the 20% OFF auto-applied too.
                            // If the cookie is already there, skip to avoid a
                            // pointless redirect through /discount/.
                            if (!isWelcomeCodeApplied()) {
                              handleWelcomeOfferClick();
                            }
                          }}
                          trackingShop={effectiveShop}
                          openSource={openSource}
                        />
                      </div>
                    </div>

                    <div className="tw:flex tw:items-center tw:gap-3 tw:my-5">
                      <div className="tw:flex-1 tw:h-px tw:bg-gray-200" />
                      <div className="tw:w-9 tw:h-9 tw:rounded-full tw:bg-gray-100 tw:flex tw:items-center tw:justify-center">
                        <span className="tw:text-xs tw:font-semibold tw:opacity-70">
                          OR
                        </span>
                      </div>
                      <div className="tw:flex-1 tw:h-px tw:bg-gray-200" />
                    </div>
                  </>
                )}

                <div className="tw:flex tw:items-start tw:gap-4">
                  <div
                    className="tw:w-16 tw:h-16 tw:rounded-full tw:shrink-0 tw:flex tw:items-center tw:justify-center"
                    style={{ backgroundColor: "#FAC8CA66" }}
                  >
                    <Gift
                      className="tw:size-9"
                      strokeWidth={1.25}
                      style={{ color: "#000" }}
                    />
                  </div>
                  <div className="tw:flex tw:flex-col tw:gap-1 tw:flex-1 tw:min-w-0">
                    <span
                      className="tw:text-base tw:font-semibold"
                      style={{ color: "#D4808A" }}
                    >
                      {welcomeCodeApplied
                        ? "Welcome Offer Applied"
                        : "New to Instant Access?"}
                    </span>
                    <span className="tw:text-xl tw:font-bold tw:leading-tight">
                      20% OFF
                    </span>
                    <span className="tw:text-sm tw:font-semibold tw:opacity-70">
                      auto-applied at checkout
                    </span>
                    {!welcomeCodeApplied && (
                      <div className="tw:self-start tw:mt-3 tw:flex tw:flex-col tw:items-center tw:gap-1">
                        <button
                          type="button"
                          onClick={handleWelcomeOfferClick}
                          className="tw:py-2 tw:px-8 tw:text-base tw:font-bold tw:rounded-md tw:border-none tw:cursor-pointer tw:transition-opacity hover:tw:opacity-90 tw:bg-[#FAC8CA]"
                          style={{ color: "var(--so-text, #523D33)" }}
                        >
                          Activate Offer
                        </button>
                        <span className="tw:text-[11px] tw:opacity-60">
                          *valid once per customer
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="tw:px-4 tw:min-h-78 tw:py-2"
                {...(showTabs
                  ? {
                      role: "tabpanel" as const,
                      "aria-labelledby":
                        panelTab === "buy-again"
                          ? "buy-again-tab"
                          : "manage-subscriptions-tab",
                    }
                  : {})}
              >
                {visibleProducts.length === 0 ? (
                  <div
                    className="tw:flex tw:flex-col tw:items-center tw:py-4 tw:px-4 tw:text-center tw:min-h-78"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    <div className="tw:flex tw:flex-col tw:items-center tw:w-full tw:my-auto">
                      {!isIdentified ? (
                        <p className="tw:text-base tw:m-0 tw:max-w-2/3">
                          {panelTab === "buy-again"
                            ? "Products you've bought will appear here"
                            : "Your subscriptions will appear here"}
                        </p>
                      ) : hasNoAccount ? (
                        <>
                          <UserRound
                            className="tw:size-10 tw:mb-3 tw:opacity-55"
                            strokeWidth={1.25}
                          />
                          <p className="tw:text-sm tw:font-medium tw:m-0">
                            {signedInEmail
                              ? `We couldn't find an account for ${signedInEmail}`
                              : "We couldn't find an account for that email"}
                          </p>
                          <p className="tw:text-sm tw:mt-2 tw:mb-0 tw:opacity-70 tw:max-w-9/10">
                            {isShopifyLoggedIn()
                              ? "If you've just placed your first order, it can take a few minutes to show up here."
                              : "Log out below and try the email you ordered with."}
                          </p>
                        </>
                      ) : panelTab === "buy-again" ? (
                        <>
                          <Package className="tw:size-10 tw:mb-3 tw:opacity-55" />
                          <p className="tw:text-sm tw:font-medium">
                            Products you&apos;ve bought will appear here
                          </p>
                        </>
                      ) : (
                        <>
                          <RefreshCw className="tw:size-10 tw:mb-3 tw:opacity-55" />
                          <p className="tw:text-sm tw:font-medium">
                            Your subscriptions will appear here
                          </p>
                        </>
                      )}
                    </div>
                    {!isIdentified && (
                      <SubscriptionSignInState
                        variant="link"
                        onSignInSuccess={onSignInSuccess}
                        trackingShop={effectiveShop}
                        openSource={openSource}
                      />
                    )}
                  </div>
                ) : null}
                {showSizeUpBanner && bestSizeUpOffer && sizeUpBannerCopy && (
                  <SizeUpSubscriptionsBanner
                    chip={bestSizeUpOffer.chip}
                    titleTemplate={sizeUpBannerCopy.titleTemplate}
                    savingsAmount={sizeUpBannerCopy.savingsAmount}
                    body={sizeUpBannerCopy.body}
                    ctaLabel={sizeUpBannerCopy.acceptLabel}
                    onSizeUp={() => {
                      if (effectiveShop) {
                        trackBuyAgainWidgetInteract(
                          "subscription_size_up_start",
                          effectiveShop,
                          {
                            source: "banner",
                            subscription_contract_id:
                              bestSizeUpOffer.candidate.subscriptionId,
                            saving_per_month:
                              bestSizeUpOffer.bundle.savingPerMonth,
                          },
                        );
                      }
                      setSizeUpDialogOfferOverride(null);
                      setSizeUpDialogOpen(true);
                    }}
                    onDismiss={() => {
                      if (effectiveShop) {
                        trackBuyAgainWidgetInteract(
                          "subscription_size_up_dismiss",
                          effectiveShop,
                        );
                      }
                      setSizeUpBannerSuppressed(true);
                    }}
                  />
                )}
                {showMergeBanner && (
                  <MergeSubscriptionsBanner
                    chips={mergeChips}
                    savesShipping={mergeSavesShippingCost}
                    savingsAmount={mergeShippingSavingsAmount}
                    onMerge={() => {
                      if (effectiveShop) {
                        trackBuyAgainWidgetInteract(
                          "subscription_merge_start",
                          effectiveShop,
                          { source: "banner" },
                        );
                      }
                      setMergeDialogOpen(true);
                    }}
                    onDismiss={() => {
                      if (effectiveShop) {
                        trackBuyAgainWidgetInteract(
                          "subscription_merge_dismiss",
                          effectiveShop,
                        );
                      }
                      setMergeBannerSuppressed(true);
                    }}
                  />
                )}
                {(panelTab === "manage"
                  ? groupSubscriptions(visibleProducts)
                  : visibleProducts.map((p) => ({
                      subscriptionId: null,
                      products: [p],
                    }))
                ).map((group: SubscriptionGroup<PurchasedProduct>) => {
                  const product = group.products[0];
                  return (
                    <ProductCard
                      key={`subscription-${group.subscriptionId ?? `single-${product.id}-${product.variant_id}-${product.shopify_subscription_id ?? "once"}`}`}
                      product={product}
                      groupProducts={group.products}
                      onCustomize={handleCustomize}
                      onBuyAgain={(p) => {
                        const shopDomain =
                          p.shop_domain ?? p.shopify_shop_domain;
                        const variantId = p.variant_id;
                        if (!shopDomain || variantId == null) return;
                        const qty = p.quantity > 0 ? p.quantity : 1;
                        if (effectiveShop) {
                          trackBuyAgainWidgetInteract(
                            "buy_now",
                            effectiveShop,
                            { productId: p.id, variantId },
                          );
                        }
                        const discountCode = getDiscountCode();
                        const url = createCheckoutUrl(
                          {
                            customer: listCustomer ?? {},
                            shop_domain: shopDomain,
                          } as Parameters<typeof createCheckoutUrl>[0],
                          variantId,
                          qty,
                          discountCode,
                        );
                        window.location.assign(url);
                      }}
                      trackingShop={effectiveShop}
                      onOpenManageSubscription={
                        openManageSubscriptionForProduct
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>

          {isIdentified && onLogout && (
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:px-4 tw:py-2.5 tw:border-t tw:border-gray-100">
              <span
                className="tw:text-xs tw:truncate tw:opacity-60"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {signedInEmail ? `Signed in as ${signedInEmail}` : "Signed in"}
              </span>
              <button
                type="button"
                onClick={onLogout}
                className="tw:shrink-0 tw:bg-transparent tw:border-none tw:p-0 tw:text-xs tw:font-semibold tw:cursor-pointer hover:tw:opacity-70"
                style={{ color: "#0396C1" }}
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </div>

      <WidgetBuyAgainSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        productId={selectedProductId}
        onManageSubscription={(productId) => {
          const p = products?.find((pr) => pr.id === productId);
          if (p) void openManageSubscriptionForProduct(p);
        }}
        initialVariantId={null}
        initialSellingPlanId={null}
      />

      <ManageSubscriptionSheet
        key={manageSheetSessionKey}
        product={manageSheetProduct}
        products={manageSheetProducts}
        open={manageSheetProduct != null}
        onOpenChange={(open) => {
          if (!open) {
            setManageSubscriptionProduct(null);
            setManageSheetInitialAction(undefined);
          }
        }}
        trackingShop={effectiveShop}
        shopifyCustomerId={listCustomer?.shopify_customer_id ?? null}
        customerEmail={listCustomer?.email ?? null}
        customerName={
          listCustomer?.full_name?.trim() ||
          [listCustomer?.first_name, listCustomer?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          null
        }
        initialAction={manageSheetInitialAction}
      />

      <MergeSubscriptionsDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        subscriptions={mergeableSubscriptions}
        customerEmail={listCustomer?.email ?? null}
        customerName={customerFullName}
        trackingShop={effectiveShop}
        onSubmitted={() => setMergeBannerSuppressed(true)}
      />

      <SizeUpReviewDialog
        open={sizeUpDialogOpen && sizeUpDialogOffer != null}
        onOpenChange={(open) => {
          setSizeUpDialogOpen(open);
          if (!open) setSizeUpDialogOfferOverride(null);
        }}
        offer={sizeUpDialogOffer}
        trackingShop={effectiveShop}
        onApplied={() => setSizeUpBannerSuppressed(true)}
      />

      {pickerAction != null && (
        <SubscriptionActionPickerDialog
          open={pickerSubscriptions.length > 0}
          onOpenChange={(open) => {
            if (!open) {
              setPickerAction(null);
              setPickerSubscriptions([]);
            }
          }}
          action={pickerAction}
          subscriptions={pickerSubscriptions}
          onSelect={(p) => {
            const action = pickerAction;
            setPickerAction(null);
            setPickerSubscriptions([]);
            void openManageSubscriptionForProduct(p, {
              initialAction: action,
            });
          }}
        />
      )}

      {unavailable != null && (
        <SubscriptionActionUnavailableDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setUnavailable(null);
          }}
          action={unavailable.action}
          reason={unavailable.reason}
          productTitle={unavailable.productTitle}
          date={unavailable.date}
        />
      )}
    </>
  );
}
